import { useCallback, useEffect, useRef, useState } from 'react';
import * as handOrderGrantAnimationDetector from './handOrderGrantAnimationDetector';
import { coalesceHandOrderGrantFlyBatch } from './coalesceHandOrderGrantFlyBatch';

const {
  collectHandOrderGrantFlyTriggerKeys,
} = handOrderGrantAnimationDetector;

function snapshotBattlefieldUnits(gameState) {
  const units = new Map();
  if (!gameState?.state) {
    return units;
  }

  const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
  allPlayerIds.forEach((playerId) => {
    const player = gameState.state[playerId];
    if (!player?.playedOrders) {
      return;
    }

    ['front', 'flank'].forEach((zone) => {
      const row = player.playedOrders[zone];
      if (!Array.isArray(row)) {
        return;
      }
      row.forEach((unit, index) => {
        if (unit?.instanceId) {
          units.set(unit.instanceId, { playerId, zone, index });
        }
      });
    });
  });

  return units;
}

function mergeGrantQueue(prev, grants) {
  if (!grants?.length) {
    return prev;
  }
  const existingKeys = new Set(prev.map((g) => g.key));
  const existingOrderIds = new Set();
  prev.forEach((g) => {
    if (g.batchOrderInstanceIds?.length) {
      g.batchOrderInstanceIds.forEach((id) => existingOrderIds.add(String(id)));
    } else if (g.orderInstanceId != null) {
      existingOrderIds.add(String(g.orderInstanceId));
    }
  });
  const merged = [...prev];
  grants.forEach((grant) => {
    const orderIds = grant.batchOrderInstanceIds?.length
      ? grant.batchOrderInstanceIds.map((id) => String(id))
      : grant?.orderInstanceId != null
        ? [String(grant.orderInstanceId)]
        : [];
    if (existingKeys.has(grant.key) || orderIds.some((sid) => existingOrderIds.has(sid))) {
      return;
    }
    orderIds.forEach((sid) => existingOrderIds.add(sid));
    merged.push(grant);
  });
  return merged;
}

/**
 * Detect fixed known-order hand grants and queue fly-from-provider animations.
 * Hides the real hand card until the fly animation completes (viewer POV only).
 */
export const useHandOrderGrantFlyAnimations = (gameState, userId) => {
  const [activeFlyGrants, setActiveFlyGrants] = useState([]);
  const [hiddenHandInstanceIds, setHiddenHandInstanceIds] = useState(() => new Set());
  const hiddenHandInstanceIdsRef = useRef(new Set());
  const [hiddenDeployFlyInstanceIds, setHiddenDeployFlyInstanceIds] = useState(() => new Set());
  const hiddenDeployFlyInstanceIdsRef = useRef(new Set());
  const previousTriggerKeysRef = useRef(new Set());
  const previousUnitsRef = useRef(new Map());
  const initializedGameIdRef = useRef(null);
  const queuedOrderInstanceIdsRef = useRef(new Set());
  const consumedFlyAnimationKeysRef = useRef(new Set());

  const queueGrants = useCallback((grants) => {
    if (!grants?.length) {
      return;
    }

    const newlyHiddenHand = [];
    const newlyHiddenDeploy = [];
    const grantsToQueue = [];

    grants.forEach((grant) => {
      if (!grant?.key || !grant?.orderInstanceId) {
        throw new Error('useHandOrderGrantFlyAnimations.queueGrants: grant key and orderInstanceId are required');
      }

      const animationKeysForGrant = grant.batchGrants?.length
        ? grant.batchGrants.map((childGrant) => childGrant.key).filter(Boolean)
        : [grant.key];

      const orderIdsToTrack = grant.batchOrderInstanceIds?.length
        ? grant.batchOrderInstanceIds.map((id) => String(id))
        : [String(grant.orderInstanceId)];

      let skipGrant = false;
      animationKeysForGrant.forEach((animKey) => {
        if (consumedFlyAnimationKeysRef.current.has(animKey)) {
          skipGrant = true;
        }
      });
      orderIdsToTrack.forEach((orderSid) => {
        if (queuedOrderInstanceIdsRef.current.has(orderSid)) {
          skipGrant = true;
        }
      });
      if (skipGrant) {
        return;
      }

      previousTriggerKeysRef.current.add(grant.key);
      animationKeysForGrant.forEach((animKey) => {
        previousTriggerKeysRef.current.add(animKey);
        consumedFlyAnimationKeysRef.current.add(animKey);
      });
      orderIdsToTrack.forEach((orderSid) => {
        queuedOrderInstanceIdsRef.current.add(orderSid);
        if (grant.destinationType === 'deploy_slot') {
          newlyHiddenDeploy.push(orderSid);
        } else {
          newlyHiddenHand.push(orderSid);
        }
      });
      grantsToQueue.push(grant);
    });

    const coalescedGrants = coalesceHandOrderGrantFlyBatch(grantsToQueue);

    if (coalescedGrants.length === 0) {
      return;
    }

    coalescedGrants.forEach((coalescedGrant) => {
      previousTriggerKeysRef.current.add(coalescedGrant.key);
      consumedFlyAnimationKeysRef.current.add(coalescedGrant.key);
    });

    newlyHiddenHand.forEach((id) => {
      hiddenHandInstanceIdsRef.current.add(id);
    });

    setHiddenHandInstanceIds((prev) => {
      const next = new Set(prev);
      newlyHiddenHand.forEach((id) => next.add(id));
      return next;
    });

    newlyHiddenDeploy.forEach((id) => {
      hiddenDeployFlyInstanceIdsRef.current.add(id);
    });

    setHiddenDeployFlyInstanceIds((prev) => {
      const next = new Set(prev);
      newlyHiddenDeploy.forEach((id) => next.add(id));
      return next;
    });

    setActiveFlyGrants((prev) => mergeGrantQueue(prev, coalescedGrants));
  }, []);

  useEffect(() => {
    if (!gameState?.state || !userId) {
      return undefined;
    }

    const gameId = gameState.gameId ?? null;
    if (gameId != null && initializedGameIdRef.current !== gameId) {
      initializedGameIdRef.current = gameId;
      previousTriggerKeysRef.current = collectHandOrderGrantFlyTriggerKeys(gameState);
      previousUnitsRef.current = snapshotBattlefieldUnits(gameState);
      queuedOrderInstanceIdsRef.current.clear();
      consumedFlyAnimationKeysRef.current.clear();
    }

    previousUnitsRef.current = snapshotBattlefieldUnits(gameState);

    return undefined;
  }, [gameState, userId]);

  useEffect(() => {
    return () => {
      previousTriggerKeysRef.current.clear();
      previousUnitsRef.current.clear();
      queuedOrderInstanceIdsRef.current.clear();
      consumedFlyAnimationKeysRef.current.clear();
    };
  }, []);

  const revealFlyGrantHandOrder = useCallback((orderInstanceId) => {
    if (orderInstanceId == null) {
      throw new Error('useHandOrderGrantFlyAnimations.revealFlyGrantHandOrder: orderInstanceId is required');
    }
    const sid = String(orderInstanceId);
    hiddenHandInstanceIdsRef.current.delete(sid);
    setHiddenHandInstanceIds((prev) => {
      if (!prev.has(sid)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(sid);
      return next;
    });
  }, []);

  const dismissFlyGrant = (key, orderInstanceId = null, batchOrderInstanceIds = null) => {
    setActiveFlyGrants((prev) => prev.filter((g) => g.key !== key));
    const idsToUnhide = batchOrderInstanceIds?.length
      ? batchOrderInstanceIds.map((id) => String(id))
      : orderInstanceId != null
        ? [String(orderInstanceId)]
        : [];
    idsToUnhide.forEach((sid) => {
      hiddenHandInstanceIdsRef.current.delete(sid);
      setHiddenHandInstanceIds((prev) => {
        if (!prev.has(sid)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(sid);
        return next;
      });
      hiddenDeployFlyInstanceIdsRef.current.delete(sid);
      setHiddenDeployFlyInstanceIds((prev) => {
        if (!prev.has(sid)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(sid);
        return next;
      });
    });
  };

  const hiddenHandInstanceIdsForFx =
    hiddenHandInstanceIdsRef.current.size > 0 ? hiddenHandInstanceIdsRef.current : null;
  const hiddenDeployFlyInstanceIdsForFx =
    hiddenDeployFlyInstanceIdsRef.current.size > 0 ? hiddenDeployFlyInstanceIdsRef.current : null;

  return {
    activeFlyGrants,
    hiddenHandInstanceIds: hiddenHandInstanceIdsForFx,
    hiddenDeployFlyInstanceIds: hiddenDeployFlyInstanceIdsForFx,
    dismissFlyGrant,
    revealFlyGrantHandOrder,
    queueGrants,
  };
};
